import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  NgZone,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { FwTooltipComponent } from '@flywheel-io/vision/components/tooltip/tooltip.component';

@Component({
  selector: 'fw-ellipsis',
  styleUrls: ['./fw-ellipsis.component.scss'],
  templateUrl: './fw-ellipsis.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FwEllipsisComponent implements OnDestroy {
  // Signals for reactive inputs
  endCapLength = input<number>(8);
  text = input.required<string>();
  tooltip = input<string | undefined>(undefined);
  tooltipDisabled = input(false);
  tooltipPosition = input<FwTooltipComponent['position']>('below');
  showTooltipOnlyEllipsized = input(false);
  tooltipMaxWidth = input(300);

  @ViewChild('ellipsisTmpl', { static: false }) ellipsisSpan: ElementRef<HTMLElement>;
  @ViewChild('endcapTmpl', { static: false }) endcapSpan: ElementRef<HTMLElement>;

  // Internal signals
  private containerWidth = signal(0);
  protected shouldEllipsis = signal(false);

  // Computed signals for derived state
  protected displayText = computed(() => {
    const currentText = this.text();
    if (!currentText) {
      return { main: '', end: '' };
    }

    // Always slice for ellipsis calculation (like original logic)
    const endCapLength = this.endCapLength(); // Fixed 8 chars as in original
    return {
      main: currentText.slice(0, -endCapLength),
      end: currentText.slice(-endCapLength),
    };
  });

  protected effectiveTooltip = computed(() => {
    return this.tooltip() || this.text();
  });

  private resizeTimeout: number | null = null;

  constructor(
    protected el: ElementRef<HTMLElement>,
    private ngZone: NgZone
  ) {
    effect(() => {
      // React to text changes
      this.text();
      this.scheduleResize();
    });
  }

  ngOnDestroy(): void {
    if (this.resizeTimeout) {
      cancelAnimationFrame(this.resizeTimeout);
    }
  }

  protected onResize(): void {
    // Debounce resize events for better performance
    this.scheduleResize();
  }

  private scheduleResize(): void {
    if (this.resizeTimeout) {
      cancelAnimationFrame(this.resizeTimeout);
    }

    this.ngZone.runOutsideAngular(() => {
      this.resizeTimeout = requestAnimationFrame(() => {
        this.checkEllipsis();
      });
    });
  }

  private checkEllipsis(): void {
    const newContainerWidth = this.el.nativeElement.clientWidth;
    const currentContainerWidth = this.containerWidth();

    // Skip if container width hasn't changed
    if (newContainerWidth === currentContainerWidth) {
      return;
    }

    this.containerWidth.set(newContainerWidth);

    if (!this.ellipsisSpan?.nativeElement || newContainerWidth === 0) {
      this.shouldEllipsis.set(false);
      return;
    }

    const textWidth = this.getTextWidth(this.displayText().main);
    const endcapWidth = this.endcapSpan?.nativeElement.scrollWidth || 0;
    const totalWidth = textWidth + endcapWidth;

    this.shouldEllipsis.set(totalWidth > newContainerWidth);
  }

  private getTextWidth = (text: string): number => text
    ? this.ellipsisSpan?.nativeElement.scrollWidth || 0
    : 0
}
